import React from "react";
import { Container, Row, Col, Card, CardBody, Badge, Button } from "reactstrap";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const lastUpdated = "September 29, 2026";

  return (
    <div className="account-pages pt-4 pb-5 bg-light min-vh-100 style-privacy-page">
      {/* Top Navigation Bar */}
      <Container>
        <Row className="align-items-center mb-4">
          <Col xs="6">
            <div className="d-flex align-items-center gap-2">
              <div
                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                style={{ width: "42px", height: "42px", fontSize: "20px", fontWeight: "bold" }}
              >
                RS
              </div>
              <div>
                <h5 className="mb-0 fw-bold text-dark">Realty Shopee CRM</h5>
                <small className="text-muted">Official Privacy & Data Protection Policy</small>
              </div>
            </div>
          </Col>
          <Col xs="6" className="text-end">
            <Link to="/login" className="btn btn-outline-primary btn-sm rounded-pill px-3 shadow-sm">
              <i className="mdi mdi-login me-1"></i> Back to Login
            </Link>
          </Col>
        </Row>
      </Container>

      {/* Main Content Card */}
      <Container>
        <Card className="border-0 shadow-lg rounded-4 overflow-hidden mb-4">
          {/* Header Banner */}
          <div
            className="p-5 text-white position-relative"
            style={{
              background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            }}
          >
            <Row className="align-items-center">
              <Col md="8">
                <Badge color="light" className="text-primary fw-semibold mb-2 px-3 py-2 rounded-pill">
                  <i className="mdi mdi-shield-check me-1"></i> Public Compliance Document
                </Badge>
                <h2 className="display-6 fw-bold text-white mb-2">Privacy Policy & Data Security</h2>
                <p className="text-white-50 mb-0">
                  Transparency and security in how Realty Shopee CRM collects, uses, and safeguards customer data and Meta Lead Ads integrations.
                </p>
              </Col>
              <Col md="4" className="text-md-end mt-3 mt-md-0">
                <div className="bg-white bg-opacity-10 backdrop-blur rounded-3 p-3 d-inline-block text-start">
                  <small className="text-white-50 d-block">Effective Date</small>
                  <strong className="text-white">{lastUpdated}</strong>
                </div>
              </Col>
            </Row>
          </div>

          <CardBody className="p-4 p-md-5">
            <Row>
              {/* Quick Jump Sidebar */}
              <Col lg="3" className="mb-4 mb-lg-0">
                <div className="sticky-top" style={{ top: "20px" }}>
                  <h6 className="text-uppercase text-muted fw-bold mb-3 fs-12 tracking-wider">Table of Contents</h6>
                  <div className="list-group list-group-flush border-0 rounded-3 shadow-sm">
                    <a href="#section-1" className="list-group-item list-group-item-action border-0 py-2.5 px-3 text-dark fs-14 fw-medium">
                      <i className="mdi mdi-information-outline text-primary me-2"></i> 1. Information Collected
                    </a>
                    <a href="#section-2" className="list-group-item list-group-item-action border-0 py-2.5 px-3 text-dark fs-14 fw-medium">
                      <i className="mdi mdi-cogs text-success me-2"></i> 2. How We Use Data
                    </a>
                    <a href="#section-3" className="list-group-item list-group-item-action border-0 py-2.5 px-3 text-dark fs-14 fw-medium">
                      <i className="mdi mdi-facebook text-info me-2"></i> 3. Meta Lead Ads Data
                    </a>
                    <a href="#section-4" className="list-group-item list-group-item-action border-0 py-2.5 px-3 text-dark fs-14 fw-medium">
                      <i className="mdi mdi-lock-outline text-warning me-2"></i> 4. Storage & Security
                    </a>
                    <a href="#section-5" className="list-group-item list-group-item-action border-0 py-2.5 px-3 text-dark fs-14 fw-medium">
                      <i className="mdi mdi-delete-outline text-danger me-2"></i> 5. Data Deletion Rights
                    </a>
                    <a href="#section-6" className="list-group-item list-group-item-action border-0 py-2.5 px-3 text-dark fs-14 fw-medium">
                      <i className="mdi mdi-email-outline text-primary me-2"></i> 6. Contact & Support
                    </a>
                  </div>

                  <div className="card border-0 bg-primary bg-opacity-10 rounded-3 p-3 mt-4 text-center">
                    <i className="mdi mdi-shield-lock-outline display-5 text-primary mb-2"></i>
                    <h6 className="fw-bold text-primary mb-1">100% Secure Platform</h6>
                    <small className="text-muted">Your data is never sold or shared with unauthorized third parties.</small>
                  </div>
                </div>
              </Col>

              {/* Policy Content */}
              <Col lg="9">
                {/* Section 1 */}
                <div id="section-1" className="mb-5 pb-3 border-bottom">
                  <h4 className="fw-bold text-dark mb-3">
                    <span className="badge bg-primary me-2">1</span> Information We Collect
                  </h4>
                  <p className="text-muted leading-relaxed">
                    Realty Shopee CRM collects minimal and necessary information required to deliver CRM lead management services, property inquiry responses, and real-time lead distribution.
                  </p>
                  <ul className="text-muted mb-0">
                    <li className="mb-2"><strong>Personal Contact Details:</strong> Name, Email Address, Phone Number, and Property Preferences submitted through inquiry forms.</li>
                    <li className="mb-2"><strong>Meta Lead Ads Data:</strong> Leads received via authorized Facebook Lead Generation forms linked to your business Facebook Pages.</li>
                    <li className="mb-2"><strong>System Usage Logs:</strong> IP address, browser type, and activity logs for security auditing and platform reliability.</li>
                  </ul>
                </div>

                {/* Section 2 */}
                <div id="section-2" className="mb-5 pb-3 border-bottom">
                  <h4 className="fw-bold text-dark mb-3">
                    <span className="badge bg-success me-2">2</span> How We Use Your Information
                  </h4>
                  <p className="text-muted leading-relaxed">
                    Information processed within Realty Shopee CRM is strictly utilized for core operational purposes:
                  </p>
                  <div className="row g-3">
                    <Col md="6">
                      <div className="p-3 bg-light rounded-3 border h-100">
                        <i className="mdi mdi-account-convert text-primary fs-20 mb-2 d-block"></i>
                        <h6 className="fw-bold text-dark mb-1">Lead Assignment</h6>
                        <small className="text-muted">Automatically routing property inquiries to assigned sales representatives for immediate client follow-up.</small>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="p-3 bg-light rounded-3 border h-100">
                        <i className="mdi mdi-bell-ring-outline text-success fs-20 mb-2 d-block"></i>
                        <h6 className="fw-bold text-dark mb-1">Notifications & Updates</h6>
                        <small className="text-muted">Sending automated SMS/email status alerts regarding inquiry updates and site visit schedules.</small>
                      </div>
                    </Col>
                  </div>
                </div>

                {/* Section 3 */}
                <div id="section-3" className="mb-5 pb-3 border-bottom">
                  <h4 className="fw-bold text-dark mb-3">
                    <span className="badge bg-info me-2">3</span> Meta (Facebook) Lead Ads Integration
                  </h4>
                  <p className="text-muted leading-relaxed">
                    Realty Shopee CRM integrates with Meta Graph API (v25.0) to fetch lead information from authorized Facebook Pages and Forms:
                  </p>
                  <div className="alert alert-info border-0 rounded-3 d-flex align-items-center p-3 mb-3">
                    <i className="mdi mdi-facebook fs-24 me-3"></i>
                    <div>
                      <strong className="d-block text-dark mb-1">Meta Data Compliance Guarantee</strong>
                      <span className="fs-13">We only access Leadgen data explicitly authorized by Facebook Page Administrators via OAuth permission dialogs. Tokens are securely encrypted and automatically refreshed.</span>
                    </div>
                  </div>
                </div>

                {/* Section 4 */}
                <div id="section-4" className="mb-5 pb-3 border-bottom">
                  <h4 className="fw-bold text-dark mb-3">
                    <span className="badge bg-warning me-2">4</span> Data Storage & Encryption
                  </h4>
                  <p className="text-muted leading-relaxed">
                    Security is central to our engineering practices. All data in transit and at rest is protected using industry-standard protocols:
                  </p>
                  <Row className="g-3">
                    <Col md="4">
                      <div className="border rounded-3 p-3 text-center bg-white shadow-sm">
                        <i className="mdi mdi-shield-key-outline text-warning display-6 mb-2"></i>
                        <h6 className="fw-bold text-dark mb-1">AES-256 Encryption</h6>
                        <small className="text-muted">Sensitive OAuth access tokens are encrypted at rest.</small>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="border rounded-3 p-3 text-center bg-white shadow-sm">
                        <i className="mdi mdi-lock-clock text-info display-6 mb-2"></i>
                        <h6 className="fw-bold text-dark mb-1">HTTPS / TLS 1.3</h6>
                        <small className="text-muted">All web traffic is encrypted with strict SSL certificates.</small>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="border rounded-3 p-3 text-center bg-white shadow-sm">
                        <i className="mdi mdi-account-shield-outline text-success display-6 mb-2"></i>
                        <h6 className="fw-bold text-dark mb-1">Role Access Control</h6>
                        <small className="text-muted">Strict permission guards ensure data isolation.</small>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Section 5 */}
                <div id="section-5" className="mb-5 pb-3 border-bottom">
                  <h4 className="fw-bold text-dark mb-3">
                    <span className="badge bg-danger me-2">5</span> User Data Deletion & Revocation
                  </h4>
                  <p className="text-muted leading-relaxed">
                    You retain complete control over your data. If you wish to revoke Facebook integration or request complete removal of your data:
                  </p>
                  <ol className="text-muted">
                    <li className="mb-2"><strong>Facebook App Revocation:</strong> Navigate to Facebook ➔ Settings & Privacy ➔ Apps and Websites ➔ Remove Realty Shopee CRM access.</li>
                    <li className="mb-2"><strong>Data Deletion Request:</strong> Send an email to <strong>privacy@realtyshopee.in</strong> with your registered email or Meta User ID. All associated leads and tokens will be permanently purged within 48 hours.</li>
                  </ol>
                </div>

                {/* Section 6 */}
                <div id="section-6" className="mb-3">
                  <h4 className="fw-bold text-dark mb-3">
                    <span className="badge bg-primary me-2">6</span> Contact & Support
                  </h4>
                  <p className="text-muted leading-relaxed mb-3">
                    If you have questions regarding this Privacy Policy or data security practices, please reach out to our compliance team:
                  </p>
                  <div className="bg-light p-4 rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div>
                      <h6 className="fw-bold text-dark mb-1">Realty Shopee Compliance Office</h6>
                      <p className="text-muted mb-0 fs-14">Email: <strong>privacy@realtyshopee.in</strong> | Support: <strong>+91-9876543210</strong></p>
                    </div>
                    <a href="mailto:privacy@realtyshopee.in" className="btn btn-primary rounded-pill px-4">
                      <i className="mdi mdi-email me-1"></i> Contact Compliance
                    </a>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>

          {/* Footer */}
          <div className="bg-light p-4 text-center border-top">
            <p className="text-muted mb-0 fs-13">
              &copy; {new Date().getFullYear()} Realty Shopee CRM. All Rights Reserved. Compliant with Meta Platform Terms & GDPR Guidelines.
            </p>
          </div>
        </Card>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
